import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { setGlobalSettings } from '../../../../../store';
import { useDispatch } from 'react-redux';
import {
  Switch,
  FormControlLabel,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  FormControl,
  RadioGroup,
  Radio,
  Checkbox,
  FormLabel,
  Divider,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  titleSettingsCard: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },

  subtitleSettingsCard: {
    color: theme.palette.secondary.main,
    fontSize: '0.6rem',
  },
}));

function Personalization(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const premium_features = useSelector((state) => state.global_settings.premium_features);
  const global_settings = useSelector((state) => state.global_settings);
  const qualityRadioValue = props?.settings?.presentation_quality ?? '';
  const diagnoseRadioValue = props?.settings?.presentation_lesions ?? '';
  const somePremiumDiagnostics = useMemo(
    () => premium_features.diagnostics_microcalc_module || premium_features.diagnostics_opacities_module,
    [premium_features]
  );

  const viewBoxStyle = {
    backgroundColor: '#e3eaf5',
    border: '1px solid #b0c4de',
    borderRadius: '4px',
    padding: '8px 4px',
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#3a5a8c',
  };

  return (
    <Grid container direction='column' spacing={4}>
      {/* Display setting */}
      <Grid item>
        <Card elevation={1}>
          <CardHeader
            disableTypography={true}
            classes={{
              action: classes.action,
            }}
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Settings.display')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: '0px' }}>
            <FormControl component='fieldset' style={{ width: '100%' }}>
              <FormLabel color='secondary'>{t('Settings.projections')}</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!props.settings.crop_to_breast}
                    onChange={(e) => props.updateSettings('crop_to_breast', e.target.checked)}
                    name='crop_to_breast'
                  />
                }
                label={t('Settings.crop_to_breast')}
              />
            </FormControl>
            {somePremiumDiagnostics && (
              <>
                <Divider style={{ margin: '10px' }} />
                <FormControl
                  component='fieldset'
                  style={{
                    width: '100%',
                  }}>
                  <FormLabel color='secondary'>b-DIAGNOSTICS</FormLabel>
                  <RadioGroup
                    aria-label='display'
                    name='display'
                    value={diagnoseRadioValue}
                    onChange={(e) => props.updateDiagnosticsPresentationDisplay(e)}
                    row
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-evenly',
                    }}>
                    <FormControlLabel value='contour' control={<Radio />} label={t('Settings.contour')} />
                    <FormControlLabel value='overlay' control={<Radio />} label={t('Settings.overlays')} />
                    <FormControlLabel value='boxes' control={<Radio />} label={t('Settings.only_boxes')} />
                  </RadioGroup>
                </FormControl>
              </>
            )}
            <Divider style={{ margin: '10px' }} />
            <FormControl
              component='fieldset'
              style={{
                width: '100%',
              }}>
              <FormLabel color='secondary'>b-QUALITY</FormLabel>
              <RadioGroup
                aria-label='display'
                name='display'
                value={qualityRadioValue.replace('_boxes', '')}
                onChange={(e) => props.updateQualityPresentationDisplay(e)}
                row
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-evenly',
                }}>
                <FormControlLabel value='contour' control={<Radio />} label={t('Settings.contour')} />
                <FormControlLabel value='overlay' control={<Radio />} label={t('Settings.overlays')} />
                <FormControlLabel value='boxes' control={<Radio />} label={t('Settings.only_boxes')} />
              </RadioGroup>
              <div
                style={{
                  width: '70%',
                  display: 'flex',
                  justifyContent: 'center',
                  height: '30px',
                }}>
                {(props?.settings?.presentation_quality?.includes('contour') ||
                  props?.settings?.presentation_quality?.includes('overlay')) && (
                  <FormControlLabel
                    value={'_boxes'}
                    control={
                      <Checkbox
                        checked={props?.settings?.presentation_quality?.includes('_boxes')}
                        onChange={(e) => props.updateQualityPresentationDisplay(e)}
                      />
                    }
                    label={t('Settings.with_boxes')}
                  />
                )}
              </div>
            </FormControl>
          </CardContent>
        </Card>
      </Grid>

      {/* Change view position */}
      <Grid item xs={12}>
        <Card elevation={1}>
          <CardHeader
            disableTypography={true}
            classes={{
              action: classes.action,
            }}
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Settings.change_view_position')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: '0px' }}>
            <RadioGroup
              aria-label='inverted_view_position'
              name='inverted_view_position'
              value={props.settings.inverted_view_position ? 'inverted' : 'default'}
              onChange={props.updateSettings}
              style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', gap: '16px' }}>
              {/* Default view position */}
              <Card
                variant='outlined'
                style={{
                  flex: 1,
                  padding: '12px',
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: !props.settings.inverted_view_position && 'rgb(6 6 6 / 13%) 0px 7px 29px 0px',
                }}
                onClick={() => {
                  props.updateSettings('inverted_view_position', false);
                }}>
                <FormControlLabel
                  value='default'
                  control={<Radio color='primary' />}
                  label={<Typography variant='subtitle2'>{t('Settings.default_view_position')}</Typography>}
                />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4px',
                    margin: '8px auto',
                    maxWidth: '160px',
                  }}>
                  <div style={viewBoxStyle}>RCC</div>
                  <div style={viewBoxStyle}>LCC</div>
                  <div style={viewBoxStyle}>RMLO</div>
                  <div style={viewBoxStyle}>LMLO</div>
                </div>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  style={{ display: 'block', textAlign: 'center' }}>
                  {t('Settings.default_view_position_desc')}
                </Typography>
              </Card>

              {/* Inverted view position */}
              <Card
                variant='outlined'
                style={{
                  flex: 1,
                  padding: '12px',
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: props.settings.inverted_view_position && 'rgb(6 6 6 / 13%) 0px 7px 29px 0px',
                }}
                onClick={() => {
                  props.updateSettings('inverted_view_position', true);
                }}>
                <FormControlLabel
                  value='inverted'
                  control={<Radio color='primary' />}
                  label={<Typography variant='subtitle2'>{t('Settings.inverted_view_position')}</Typography>}
                />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4px',
                    margin: '8px auto',
                    maxWidth: '160px',
                  }}>
                  <div style={viewBoxStyle}>RMLO</div>
                  <div style={viewBoxStyle}>LMLO</div>
                  <div style={viewBoxStyle}>RCC</div>
                  <div style={viewBoxStyle}>LCC</div>
                </div>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  style={{ display: 'block', textAlign: 'center' }}>
                  {t('Settings.inverted_view_position_desc')}
                </Typography>
              </Card>
            </RadioGroup>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default Personalization;
